import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../services/api';
import {
  Building2, CreditCard, Users, TrendingUp, DollarSign, Clock,
  Ban, CheckCircle, ArrowUpRight, ArrowDownRight, Activity,
  BarChart3, AlertTriangle, RefreshCw, Eye,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/* ============================================================
   STAT CARD
   ============================================================ */
function StatCard({ icon: Icon, label, value, sub, color, trend, onClick }) {
  return (
    <div className="sa-stat-card" onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
      <div className="sa-stat-icon" style={{ background: color }}>
        <Icon size={18} />
      </div>
      <div className="sa-stat-body">
        <div className="sa-stat-label">{label}</div>
        <div className="sa-stat-value">{value}</div>
        {sub && <div className="sa-stat-sub">{sub}</div>}
      </div>
      {trend !== undefined && (
        <span className={`sa-stat-trend ${trend >= 0 ? 'up' : 'down'}`}>
          {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
  );
}

/* ============================================================
   SUPER ADMIN DASHBOARD
   ============================================================ */
export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activity, setActivity] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, c, a, p] = await Promise.all([
        subscriptionsAPI.stats().catch(() => null),
        subscriptionsAPI.companies({}).catch(() => []),
        subscriptionsAPI.activity().catch(() => []),
        subscriptionsAPI.plans().catch(() => []),
      ]);
      setStats(s);
      setCompanies(c);
      setActivity(a);
      setPlans(p);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  const activeCompanies = companies.filter(c => c.sub_status === 'active').length;
  const trialCompanies = companies.filter(c => c.sub_status === 'trialing').length;
  const expiredCompanies = companies.filter(c => c.sub_status === 'expired' || c.sub_status === 'cancelled').length;
  const totalUsers = companies.reduce((sum, c) => sum + (c.active_users || 0), 0);
  const pendingCount = companies.filter(c => c.sub_status === 'pending').length;

  return (
    <div>
      {/* Welcome header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Platform Overview
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
          Welcome back. Here's what's happening with your platform today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="sa-stats-grid">
        <StatCard
          icon={Building2}
          label="Total Companies"
          value={stats?.total_companies || companies.length}
          color="#3b82f6"
          onClick={() => navigate('/super-admin/companies')}
        />
        <StatCard
          icon={CheckCircle}
          label="Active Subscriptions"
          value={stats?.active_subscriptions || activeCompanies}
          sub={`${activeCompanies} companies`}
          color="#16a34a"
          onClick={() => navigate('/super-admin/subscriptions')}
        />
        <StatCard
          icon={Clock}
          label="Trialing"
          value={stats?.trialing || trialCompanies}
          sub="in trial period"
          color="#8b5cf6"
          onClick={() => navigate('/super-admin/subscriptions')}
        />
        <StatCard
          icon={AlertTriangle}
          label="Expired / Cancelled"
          value={expiredCompanies}
          sub="needs attention"
          color="#ef4444"
        />
        <StatCard
          icon={Users}
          label="Total Users"
          value={totalUsers}
          sub="across all companies"
          color="#0ea5e9"
          onClick={() => navigate('/super-admin/users')}
        />
        <StatCard
          icon={DollarSign}
          label="Monthly Recurring Revenue"
          value={fmt(stats?.mrr)}
          sub="from active subs"
          color="#16a34a"
          onClick={() => navigate('/super-admin/payments')}
        />
        <StatCard
          icon={TrendingUp}
          label="Total Revenue"
          value={fmt(stats?.total_revenue)}
          sub="all time"
          color="#7c3aed"
          onClick={() => navigate('/super-admin/payments')}
        />
        <StatCard
          icon={CreditCard}
          label="Pending Confirmations"
          value={stats?.pending_confirmations || pendingCount}
          sub="awaiting approval"
          color="#f59e0b"
          onClick={() => navigate('/super-admin/subscriptions')}
        />
      </div>

      {/* Two-column layout */}
      <div className="sa-two-col">
        {/* Plan Distribution */}
        <div className="sa-card">
          <div className="sa-card-header">
            <h3 className="sa-card-title">Plan Distribution</h3>
            <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate('/super-admin/plans')}>
              Manage Plans
            </button>
          </div>
          <div className="sa-card-body">
            {plans.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>No plans configured</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plans.map((plan, i) => {
                  const count = companies.filter(c => c.plan_name === plan.name).length;
                  const total = companies.length || 1;
                  const pct = Math.round((count / total) * 100);
                  const colors = ['#3b82f6', '#16a34a', '#8b5cf6', '#f59e0b', '#ef4444'];
                  return (
                    <div key={plan.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{plan.name}</span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{count} ({pct}%)</span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', width: `${pct}%`,
                          background: colors[i % colors.length], borderRadius: 3,
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="sa-card">
          <div className="sa-card-header">
            <h3 className="sa-card-title">Recent Activity</h3>
            <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate('/super-admin/activity')}>
              View All
            </button>
          </div>
          <div className="sa-card-body" style={{ padding: '12px 20px' }}>
            {activity.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>No activity yet</p>
            ) : (
              <div className="sa-timeline">
                {activity.slice(0, 8).map((a, i) => (
                  <div key={i} className="sa-timeline-item">
                    <div className="sa-timeline-dot" style={{
                      background: a.action === 'activated' || a.action === 'confirmed' ? '#16a34a' :
                                  a.action === 'cancelled' ? '#ef4444' :
                                  a.action === 'created' ? '#3b82f6' : '#f59e0b'
                    }} />
                    <div className="sa-timeline-content">
                      <div className="sa-timeline-text">
                        <strong>{a.company_name}</strong> — {a.action}
                        {a.performed_by_name && <span style={{ color: '#94a3b8' }}> by {a.performed_by_name}</span>}
                      </div>
                      <div className="sa-timeline-time">{fmtDate(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Companies */}
      <div className="sa-card">
        <div className="sa-card-header">
          <h3 className="sa-card-title">Recent Companies</h3>
          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate('/super-admin/companies')}>
            View All Companies
          </button>
        </div>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Users</th>
                <th>Created</th>
                <th className="sa-text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 && (
                <tr className="sa-empty-row"><td colSpan={6}>No companies registered yet</td></tr>
              )}
              {companies.slice(0, 5).map(co => (
                <tr key={co.id}>
                  <td>
                    <div className="sa-company-cell">
                      <strong>{co.name}</strong>
                      <span>{co.email}</span>
                    </div>
                  </td>
                  <td>{co.plan_name || '—'}</td>
                  <td>
                    <span className={`sa-badge-status sa-badge-${co.sub_status || 'expired'}`}>
                      {co.sub_status || 'none'}
                    </span>
                  </td>
                  <td>{co.active_users || 0}</td>
                  <td>{fmtDate(co.created_at)}</td>
                  <td className="sa-text-right">
                    <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate(`/super-admin/companies/${co.id}`)}>
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
